import ActionCondition, { SpecificLinkOption } from 'cio/app-models/conditions/action';
import refinement from './refinement';
import { isNone } from '@ember/utils';
import { assert } from '@ember/debug';
import { SerializedActionCondition } from 'cio/types/conditions/condition';
import {
  SerializedLegacyAttributeFilter,
  SerializedLegacyAttributeFilterAnd,
  SerializedLegacyAttributeFilterOr,
  SerializedLegacyAttributeFilters,
} from 'cio/types/conditions/legacy-filter';

function unpackFilters(filters?: SerializedLegacyAttributeFilters | Record<string, never>) {
  return (
    (filters as SerializedLegacyAttributeFilterAnd)?.and ??
    (filters as SerializedLegacyAttributeFilterOr)?.or ?? [filters as SerializedLegacyAttributeFilter]
  );
}

export default function (json: SerializedActionCondition): ActionCondition {
  let event = json.event || {};
  let { filters, name, type } = event;
  let { times, within, inverse, isForeignEvent } = json;
  let actionType = (name || '')
    .replace('any_', '')
    .replace('-campaign', '')
    .replace(/_[^_]+$/, '');
  let action = name;
  let specificLinkOption = SpecificLinkOption.None;
  let linkHref;
  let deserializedFilter;

  unpackFilters(filters).forEach((filter: SerializedLegacyAttributeFilter) => {
    switch (filter?.field) {
      case 'href':
        deserializedFilter = deserializeSpecificLink(filter);
        if (deserializedFilter) {
          linkHref = deserializedFilter.value;
          specificLinkOption = specificLinkOptionToOperator(deserializedFilter.operator);
        }
        break;
      case 'action_name':
        // in-app specific
        deserializedFilter = deserializeInAppNamedAction(filter);
        if (deserializedFilter) {
          linkHref = deserializedFilter.value;
          specificLinkOption = specificLinkOptionToOperator(deserializedFilter.operator);
        }
        break;
      case 'template_id':
        action = `${action}_variation_${filter.value}`;
        break;
    }
  });

  if (isNone(times)) {
    times = 1;
  }

  if (isNone(within)) {
    within = 0;
  }

  if (type.includes('email')) {
    type = type.replace('email', 'action');
    actionType = 'email';
  }

  if (isForeignEvent) {
    inverse = false;
  }

  if (action.includes(`${actionType}-campaign`)) {
    action = `any_${action}`;
  }

  let condition = ActionCondition.create({
    action,
    inverse,
    linkHref,
    eventType: type,
    refinement: refinement(times, within),
    specificLink: specificLinkOption,
    type: actionType,
    isForeignEvent: isForeignEvent === true,
  });

  return condition;
}

// Specific link filter looks like this:
//
// {
//   "field":"href",
//   "operator":"contains" | "eq",
//   "value":"http://example.com",
//   "inverse":false,
// }
//
// If the filter matches, return `value`; otherwise return undefined.
function deserializeSpecificLink(filter: SerializedLegacyAttributeFilter | null) {
  return specificLinkDeserializer()(filter);
}

function deserializeInAppNamedAction(filter: SerializedLegacyAttributeFilter | null) {
  return specificLinkDeserializer('action_name')(filter);
}

function specificLinkDeserializer(expectedFieldName = 'href', expectedOperators = ['contains', 'eq']) {
  return (filter: SerializedLegacyAttributeFilter | null) => {
    if (isNone(filter)) {
      return;
    }

    if (Object.keys(filter).length === 0) {
      return;
    }

    let { field, operator, value } = filter;

    assert(`Invalid event filter object: ${JSON.stringify(filter)}`, ![field, operator, value].some(isNone));

    if (field === expectedFieldName && expectedOperators.includes(operator)) {
      return { value, operator };
    }

    return;
  };
}

function specificLinkOptionToOperator(operator: string): SpecificLinkOption {
  switch (operator) {
    case 'contains':
      return SpecificLinkOption.Contains;
    case 'eq':
      return SpecificLinkOption.Exact;
    case 'none':
      return SpecificLinkOption.None;
    default:
      throw new Error(`Unknown specific link operator: ${operator}`);
  }
}
